/**
 * AppIdeaContentLightbox - CHILD COMPONENT (NO RESPONSIVE LOGIC)
 * 
 * This component is a specialized wrapper for TextContentLightbox.
 * 
 * ARCHITECTURE RULES:
 * - This component should NOT contain responsive breakpoints (md:, lg:, sm:)
 * - All responsive behavior is controlled by TextContentLightbox (master)
 * - This component only handles App Idea-specific data transformation and rendering
 * 
 * RESPONSIVE RESPONSIBILITY:
 * - TextContentLightbox controls layout, sizing, and breakpoints
 * - This component provides content rendering only
 * 
 * If you need responsive behavior, modify TextContentLightbox, not this file!
 */
import React from 'react';
import {
  TextContentLightbox,
  TextContentAccessors,
  TextContentContext,
  TextContentSidebarContext,
} from '@/components/text-content-lightbox';
import { AppIdea } from '@/hooks/app-library';
import { AppIdeaContentLightboxProps } from './types/AppIdeaLightbox.types';
import { AppIdeaContentRenderer } from './AppIdeaContentRenderer';
import { AppIdeaSidebarRenderer } from './AppIdeaSidebarRenderer';
import { Skeleton } from '@/components/ui/skeleton';

const appIdeaAccessors: TextContentAccessors<AppIdea> = {
  getContentId: (idea) => idea.id,
  getContentTitle: (idea) => idea.name || 'App Idea',
  getContentBody: (idea) => idea.description || '',
  getContentType: () => 'research',
};

// LoadingComponent - Mimics the structure that TextContentLightbox will create
// Uses lg: breakpoint to match master component's responsive strategy
// DO NOT add other responsive classes here - let TextContentLightbox handle layout!
const LoadingComponent = () => (
    <div className="p-6 flex gap-6 h-full">
      <div className="flex-1 space-y-6">
        <div className="border-b pb-4 mb-6">
            <Skeleton className="h-8 w-3/4 mb-2" />
            <Skeleton className="h-4 w-full" />
        </div>
        <div className="space-y-8">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
        </div>
      </div>
      {/* Sidebar skeleton - matches master component's lg:w-[250px] strategy */}
      <div className="w-full lg:w-[280px] flex-shrink-0">
        <div className="flex flex-col h-full space-y-3">
            <Skeleton className="h-[60px] w-full" />
            <div className="flex-1 space-y-3">
                <Skeleton className="h-[88px] w-full" />
                <Skeleton className="h-[200px] w-full" />
            </div>
            <Skeleton className="h-[88px] w-full" />
        </div>
      </div>
    </div>
);

interface ExtendedAppIdeaContentLightboxProps extends AppIdeaContentLightboxProps {
  onDelete?: (idea: AppIdea) => Promise<void>;
  onDownload?: (idea: AppIdea) => void;
}

export const AppIdeaContentLightbox: React.FC<ExtendedAppIdeaContentLightboxProps> = ({
  idea,
  isOpen,
  onClose,
  allIdeas = [],
  onNavigate,
  loadMore,
  hasNextPage = false,
  isFetchingNextPage = false,
  isLoading = false,
  onDelete,
  onDownload,
}) => {
  // Early return if not open to prevent unnecessary hook calls
  if (!isOpen) {
    return null;
  }

  // Ensure we have a stable array reference
  const stableAllIdeas = React.useMemo(() => allIdeas || [], [allIdeas]);

  const renderContent = (item: AppIdea, context: TextContentContext<AppIdea>) => (
    <AppIdeaContentRenderer idea={item} context={context} />
  );

  const renderSidebar = (item: AppIdea, context: TextContentSidebarContext<AppIdea>) => (
    <AppIdeaSidebarRenderer 
      idea={item} 
      context={context} 
      onCloseCurrentLightbox={onClose}
      onDelete={onDelete}
      onDownload={onDownload}
    />
  );
  
  return (
    <TextContentLightbox
      item={idea}
      isOpen={isOpen}
      onClose={onClose}
      allItems={stableAllIdeas}
      onNavigate={onNavigate}
      loadMore={loadMore}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      accessors={appIdeaAccessors}
      renderContent={renderContent}
      renderSidebar={renderSidebar}
      isLoading={isLoading}
      loadingComponent={<LoadingComponent />}
    />
  );
};
